import { isContaCaixa } from "./contaCaixaReforcoUtils";
import { CurrencyInput } from "@/components";
import { useClinic } from "@/components/Context/ClinicaContext";
import { useUser } from "@/components/Context/UserContext";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import {
  FaCalendarAlt,
  FaExclamationTriangle,
  FaHospital,
  FaInfoCircle,
  FaSync,
  FaUser,
  FaWallet,
} from "react-icons/fa";
import { formatCurrencyBR } from "@/utils/formatUtils";
import { request } from "@/services/fetch";
import { sendReforcoOperation } from '@/services/Financas/reforcoOperation';
import useFinancialRefresh, { notifyFinancialUpdate } from '@/hooks/useFinancialRefresh';
import ContaCaixaReforcoSelect from "./ContaCaixaReforcoSelect";
import {
  buildOpcaoCaixa,
  CAIXA_CLINICA_DEFAULT,
  getContaDisplayName,
  getSaldoContaRaw,
  isSelecaoCaixaOperacional,
  resolveContaDestinoReforco,
} from "./contaCaixaReforcoUtils";

const ESPECIE_OPCOES = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "cartao_credito", label: "Cartão de Crédito" },
  { value: "cartao_debito", label: "Cartão de Débito" },
  { value: "pix", label: "PIX" },
  { value: "transferencia", label: "Transferência" },
  { value: "outro", label: "Outro" },
];

function ReforcoModal({ onSuccess, children, disclosure }) {
  const internalDisclosure = useDisclosure();
  const { isOpen, onOpen, onClose } = disclosure ?? internalDisclosure;

  const OBS_MAX = 500;
  /** Altura mínima alinhada entre conta/caixa e coluna espécie+valor */
  const FORM_FIELD_ROW_MIN_H = "120px";

  const [data, setData] = useState(new Date());
  const [atendente, setAtendente] = useState("");
  const [contaCaixa, setContaCaixa] = useState("");
  const [contasBancarias, setContasBancarias] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);
  const [especie, setEspecie] = useState("dinheiro");
  const [valor, setValor] = useState(0);
  const [observacao, setObservacao] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingCaixa, setCheckingCaixa] = useState(false);
  const [caixaAberto, setCaixaAberto] = useState(false);
  const [saldoDisponivel, setSaldoDisponivel] = useState(0);
  const [saldoCaixaReal, setSaldoCaixaReal] = useState(0);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [loadingSaldoCaixa, setLoadingSaldoCaixa] = useState(false);
  const [clinica, setClinica] = useState("");
  const [pending, setPending] = useState(null);
  const sendingRef = useRef(false);
  const financialRevision = useFinancialRefresh();

  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const toast = useToast();
  const formattedDate = format(data, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  const nomeClinicaSelecionada = selectedClinic
    ? selectedClinic.razaoSocial || selectedClinic.nome || ""
    : "";

  const getUserId = () => {
    if (user?._id) return user._id;
    if (user?.id) return user.id;
    if (user?.userId) return user.userId;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const p = JSON.parse(stored);
        return (
          p?.user?._id ||
          p?.user?.id ||
          p?.colaborador?._id ||
          p?.profissionalSaude?._id ||
          p?._id ||
          p?.id
        );
      }
    } catch (e) { }
    return null;
  };

  const getUserName = () => {
    if (user?.nome) return user.nome;
    if (user?.name) return user.name;
    if (user?.nomeCompleto) return user.nomeCompleto;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const p = JSON.parse(stored);
        const u = p?.user || p?.colaborador || p?.profissionalSaude || p;
        return u?.nome || u?.name || "Usuário";
      }
    } catch (e) { }
    return "Usuário";
  };

  const contasAtivas = useMemo(
    () => {
      const caixas = contasBancarias.filter(isContaCaixa);
      return caixas.length === 1 && caixas[0].ativa !== false ? caixas : [];
    },
    [contasBancarias],
  );

  const opcaoCaixa = useMemo(
    () => buildOpcaoCaixa(contasAtivas, caixaAberto),
    [contasAtivas, caixaAberto],
  );

  const nomeCaixaOperacional = opcaoCaixa
    ? getContaDisplayName(opcaoCaixa)
    : CAIXA_CLINICA_DEFAULT;

  const selecionouCaixaOperacional = isSelecaoCaixaOperacional(
    contaCaixa,
    opcaoCaixa,
  );

  const saldoApos = saldoDisponivel + valor;
  const feedbackNegativo = valor > 0 && saldoApos < 0;
  const caixaFechadoSelecionado =
    selecionouCaixaOperacional && !caixaAberto;
  const resumoVisual = useMemo(() => {
    if (caixaFechadoSelecionado) {
      return {
        borderColor: "orange.200",
        bg: "orange.50",
        icon: FaExclamationTriangle,
        iconColor: "orange.500",
        title: "Caixa fechado",
        titleColor: "orange.800",
      };
    }
    if (feedbackNegativo) {
      return {
        borderColor: "red.200",
        bg: "red.50",
        icon: FaExclamationTriangle,
        iconColor: "red.500",
        title: "Atenção ao saldo",
        titleColor: "red.800",
      };
    }
    if (valor > 0) {
      return {
        borderColor: "green.200",
        bg: "green.50",
        icon: FaInfoCircle,
        iconColor: "green.500",
        title: "Resumo da operação",
        titleColor: "green.800",
      };
    }
    return {
      borderColor: "blue.100",
      bg: "blue.50",
      icon: FaInfoCircle,
      iconColor: "blue.500",
      title: "Resumo da operação",
      titleColor: "blue.800",
    };
  }, [caixaFechadoSelecionado, feedbackNegativo, valor]);

  const fetchSaldoCaixaReal = useCallback(async () => {
    if (!clinica || !caixaAberto) {
      setSaldoCaixaReal(0);
      return;
    }
    try {
      setLoadingSaldoCaixa(true);
      const resumo = await request(`api/financas/caixa/resumo-dia/${clinica}`);
      // API do caixa retorna valores em centavos (mesmo padrão do FechamentoCaixa)
      const saldo = Number(resumo.saldoCalculado ?? 0);
      setSaldoCaixaReal(saldo);
      return saldo;
    } catch {
      setSaldoCaixaReal(0);
      return 0;
    } finally {
      setLoadingSaldoCaixa(false);
    }
  }, [clinica, caixaAberto]);

  const fetchSaldoDisponivel = useCallback(
    async (contaId, isCaixaOpen, opcaoCaixa, saldoCaixaAtual) => {
      if (!clinica || !contaId) {
        setSaldoDisponivel(0);
        return;
      }

      if (isSelecaoCaixaOperacional(contaId, opcaoCaixa)) {
        if (isCaixaOpen) {
          setLoadingSaldo(true);
          const saldo =
            saldoCaixaAtual != null
              ? saldoCaixaAtual
              : await fetchSaldoCaixaReal();
          setSaldoDisponivel(Number(saldo ?? 0));
          setLoadingSaldo(false);
          return;
        }
        const contaOp = opcaoCaixa?.isVirtualCaixa
          ? null
          : opcaoCaixa;
        if (contaOp) {
          setSaldoDisponivel(getSaldoContaRaw(contaOp));
        } else {
          setSaldoDisponivel(0);
        }
        return;
      }

      const conta = contasAtivas.find((c) => String(c._id) === String(contaId));
      if (!conta) {
        setSaldoDisponivel(0);
        return;
      }
      setSaldoDisponivel(getSaldoContaRaw(conta));
    },
    [clinica, contasAtivas, fetchSaldoCaixaReal],
  );

  useEffect(() => {
    if (isOpen) {
      setClinica(selectedClinic?._id || "");
      setAtendente(getUserName());
      setData(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getUserName lê contexto/localStorage
  }, [isOpen, selectedClinic]);

  useEffect(() => {
    if (!isOpen) {
      setValor(0);
      setObservacao("");
      setContaCaixa("");
      setEspecie("dinheiro");
      setSaldoDisponivel(0);
      setSaldoCaixaReal(0);
      setCaixaAberto(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !clinica) return;
    let cancelled = false;
    setLoadingContas(true);
    request(`api/financas/contabancaria/clinica/${clinica}`)
      .then((list) => {
        if (!cancelled) setContasBancarias(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setContasBancarias([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingContas(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, clinica, financialRevision]);

  useEffect(() => {
    if (!isOpen || !caixaAberto || !clinica) return;
    fetchSaldoCaixaReal();
  }, [isOpen, caixaAberto, clinica, fetchSaldoCaixaReal, financialRevision]);

  useEffect(() => {
    if (!isOpen) return;
    setContaCaixa(!loadingContas && opcaoCaixa ? String(opcaoCaixa._id) : '');
  }, [isOpen, loadingContas, opcaoCaixa]);

  useEffect(() => {
    if (!isOpen || !contaCaixa) return;
    const contaId = contaCaixa;
    const aberto = caixaAberto;
    const opcao = opcaoCaixa;
    const saldoCaixa = saldoCaixaReal;

    startTransition(() => {
      fetchSaldoDisponivel(contaId, aberto, opcao, saldoCaixa);
    });
  }, [
    isOpen,
    contaCaixa,
    caixaAberto,
    opcaoCaixa,
    saldoCaixaReal,
    fetchSaldoDisponivel,
  ]);

  const checkCaixaStatus = useCallback(async () => {
    if (!clinica) return;

    try {
      setCheckingCaixa(true);
      const statusData = await request(`api/financas/caixa/status/${clinica}`);
      setCaixaAberto(Boolean(statusData.aberto));
    } catch (error) {
      setCaixaAberto(false);
      toast({
        title: "Erro",
        description: "Não foi possível verificar o status do caixa.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckingCaixa(false);
    }
  }, [clinica, toast]);

  useEffect(() => {
    if (isOpen && clinica) {
      checkCaixaStatus();
    }
  }, [isOpen, clinica, checkCaixaStatus]);

  const parseValue = (v) => Number(v) || 0;

  const pendingKey = `osteomed:reforco-pendente:${getUserId() || ''}:${clinica}`;
  useEffect(() => {
    if (!isOpen || !clinica) return;
    try { setPending(JSON.parse(localStorage.getItem(pendingKey) || 'null')); }
    catch { setPending(null); }
  }, [isOpen, clinica, pendingKey]);

  const enviarReforco = async (operation) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setLoading(true);
    let completed = false;
    try {
      setPending(operation);
      await sendReforcoOperation(operation, pendingKey);
      completed = true;
      setPending(null);
    } catch (error) {
      if (error.status === 400) {
        setPending(null);
      }
      toast({ title: 'Não foi possível confirmar o reforço',
        description: `${error.message || 'Falha na comunicação.'} ${error.status === 400 ? '' : 'Use “Reenviar reforço pendente” para conferir o mesmo lançamento sem duplicar o valor.'}`,
        status: 'error', duration: 7000, isClosable: true });
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
    if (completed) {
      notifyFinancialUpdate(clinica);
      toast({ title: 'Reforço registrado com sucesso!', status: 'success', duration: 4000, isClosable: true });
      onClose();
      if (onSuccess) Promise.resolve().then(onSuccess).catch(() => {});
    }
  };

  const handleSubmit = async () => {
    if (sendingRef.current) return;
    if (pending) return enviarReforco(pending);
    const valorCentavos = parseValue(valor);
    if (!valorCentavos || valorCentavos <= 0) {
      toast({
        title: "Informe o valor.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!clinica) {
      toast({
        title: "Selecione uma clínica.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!contaCaixa) {
      toast({
        title: "A conta caixa da clínica não está disponível.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (selecionouCaixaOperacional && !caixaAberto) {
      toast({
        title: "Caixa fechado",
        description:
          "É necessário abrir o caixa antes de realizar um reforço.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const contaDestinoId = resolveContaDestinoReforco(contaCaixa, contasAtivas);
    if (!contaDestinoId) {
      toast({
        title: "Conta de destino não encontrada",
        description:
          "Selecione uma conta ativa cadastrada com a instituição 000 - Conta caixa.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const userId = getUserId();
    if (!userId) {
      toast({
        title: "Usuário não identificado. Faça login novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      const conta = contasBancarias.find(
        (c) => String(c._id) === String(contaDestinoId),
      );
      const nomeConta = selecionouCaixaOperacional
        ? nomeCaixaOperacional
        : conta
          ? getContaDisplayName(conta)
          : "";
      const especieLabel =
        ESPECIE_OPCOES.find((e) => e.value === especie)?.label || especie;
      const descricao = [
        observacao,
        nomeConta ? `Conta: ${nomeConta}` : "",
        `Espécie: ${especieLabel}`,
      ]
        .filter(Boolean)
        .join(" | ");

      await enviarReforco({ url: `api/financas/contabancaria/${contaDestinoId}/reforco`,
        body: { chave: crypto.randomUUID(), valor: valorCentavos, descricao, especie, caixaOperacional: selecionouCaixaOperacional } });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível registrar o reforço",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const InfoRow = ({ icon, label, children }) => (
    <HStack align="flex-start" spacing={2} w="100%">
      <Icon as={icon} color="blue.500" mt={0.5} boxSize={4} flexShrink={0} />
      <Flex
        flex={1}
        justify="space-between"
        align="flex-start"
        gap={2}
        flexWrap="wrap"
        minW={0}
      >
        <Text fontWeight="semibold" fontSize="sm" color="gray.700" whiteSpace="normal">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.800" textAlign={{ base: "left", md: "right" }} minW={0} overflowWrap="anywhere" flex={{ base: "1 1 100%", md: 1 }}>
          {children}
        </Text>
      </Flex>
    </HStack>
  );

  return (
    <>
      {typeof children === 'function' ? children({ onOpen }) : children}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        closeOnOverlayClick={false}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="lg"
          overflow="hidden"
          w={{ base: "calc(100vw - 16px)", md: "calc(100vw - 48px)" }}
          maxW="920px"
          my={{ base: 2, md: 6 }}
          maxH={{ base: "calc(100dvh - 16px)", md: "calc(100dvh - 48px)" }}
        >
          <ModalHeader
            bgGradient="linear(to-r, blue.600, blue.500)"
            color="white"
            py={2.5}
            px={4}
            pr={12}
            shadow="sm"
          >
            <HStack spacing={2}>
              <Flex
                align="center"
                justify="center"
                w={8}
                h={8}
                borderRadius="full"
                bg="whiteAlpha.300"
              >
                <Icon as={FaSync} boxSize={3.5} />
              </Flex>
              <Text fontWeight="bold" fontSize="md">
                Reforço
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={2} />

          <ModalBody py={4} px={{ base: 3, md: 4 }} minH={0} overflowY="auto" overflowWrap="anywhere">
            {pending && <Text mb={3} color="orange.700" role="status">
              Reforço pendente de confirmação: {formatCurrencyBR(pending.body.valor, true)}. {pending.body.descricao}
              {' '}O reenvio confere esse mesmo lançamento, sem somar o valor novamente.
            </Text>}
            {checkingCaixa ? (
              <Flex justify="center" py={6}>
                <Spinner color="blue.500" />
              </Flex>
            ) : (
              <VStack as="fieldset" disabled={loading || !!pending} spacing={3} align="stretch">
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  bg="gray.50"
                >
                  <Grid
                    templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }}
                    gap={{ base: 3, md: 0 }}
                    alignItems="center"
                  >
                    <GridItem minW={0} pr={{ md: 4 }} pb={{ base: 3, md: 0 }}>
                      <VStack align="stretch" spacing={1.5}>
                        <InfoRow icon={FaCalendarAlt} label="Data/Hora:">
                          {formattedDate}
                        </InfoRow>
                        <InfoRow icon={FaHospital} label="Clínica:">
                          {nomeClinicaSelecionada || "Nenhuma clínica selecionada"}
                        </InfoRow>
                        {!clinica && (
                          <Text fontSize="xs" color="blue.600" pl={6}>
                            Selecione uma clínica no seletor da tela antes de realizar
                            o reforço.
                          </Text>
                        )}
                        <InfoRow icon={FaUser} label="Colaborador/Atendente:">
                          {atendente || getUserName()}
                        </InfoRow>
                      </VStack>
                    </GridItem>

                    <GridItem
                      minW={0}
                      pl={{ md: 4 }}
                      pt={{ base: 3, md: 0 }}
                      borderTopWidth={{ base: "1px", md: 0 }}
                      borderLeftWidth={{ base: 0, md: "1px" }}
                      borderColor="gray.300"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH={{ md: "100%" }}
                    >
                      <HStack spacing={3} align="center" justify="center">
                        <Icon
                          as={FaWallet}
                          color="blue.500"
                          boxSize={5}
                          flexShrink={0}
                        />
                        <Box textAlign="center">
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color="gray.700"
                          >
                            Saldo Disponível:
                          </Text>
                          {loadingSaldo ? (
                            <Flex justify="center" mt={1}>
                              <Spinner size="sm" color="green.500" />
                            </Flex>
                          ) : (
                            <Text
                              fontSize="xl"
                              fontWeight="bold"
                              color="green.600"
                              fontVariantNumeric="tabular-nums"
                              mt={0.5}
                              lineHeight="shorter"
                            >
                              {formatCurrencyBR(saldoDisponivel, true)}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    </GridItem>
                  </Grid>
                </Box>

                <Grid
                  templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }}
                  gap={2}
                  alignItems="stretch"
                >
                  <GridItem
                      minW={0}
                    display="flex"
                    flexDirection="column"
                    minH={{ md: FORM_FIELD_ROW_MIN_H }}
                  >
                    <ContaCaixaReforcoSelect
                      contas={contasBancarias}
                      caixaAberto={caixaAberto}
                      saldoCaixaReal={saldoCaixaReal}
                      loadingSaldoCaixa={loadingSaldoCaixa}
                      loading={loadingContas}
                      alignStretch
                    />
                  </GridItem>

                  <GridItem
                      minW={0}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    minH={{ md: FORM_FIELD_ROW_MIN_H }}
                  >
                    <FormControl mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Espécie
                      </FormLabel>
                      <Select
                        size="md"
                        value={especie}
                        onChange={(e) => setEspecie(e.target.value)}
                      >
                        {ESPECIE_OPCOES.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText mb={0} mt={0.5} fontSize="xs">
                        Selecione a espécie do reforço
                      </FormHelperText>
                    </FormControl>

                    <FormControl isRequired mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Valor
                      </FormLabel>
                      <CurrencyInput
                        value={valor}
                        onChange={(centavos) => setValor(centavos)}
                        placeholder="0,00"
                      />
                      <FormHelperText mb={0} fontSize="xs">
                        Valor a ser adicionado à conta (use vírgula para decimais)
                      </FormHelperText>
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid
                  templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }}
                  gap={3}
                  alignItems="stretch"
                >
                  <GridItem minW={0}>
                    <FormControl h="100%" display="flex" flexDirection="column" mb={0}>
                      <FormLabel mb={0.5} fontSize="sm">
                        Observação
                      </FormLabel>
                      <Box position="relative" flex="1">
                        <Textarea
                          value={observacao}
                          onChange={(e) =>
                            setObservacao(e.target.value.slice(0, OBS_MAX))
                          }
                          placeholder="Ex: Abertura do caixa"
                          rows={3}
                          maxLength={OBS_MAX}
                          h="100%"
                          minH="80px"
                          size="sm"
                        />
                        <Text
                          position="absolute"
                          bottom={2}
                          right={3}
                          fontSize="xs"
                          color="gray.400"
                        >
                          {observacao.length}/{OBS_MAX}
                        </Text>
                      </Box>
                    </FormControl>
                  </GridItem>

                  <GridItem minW={0} display="flex" flexDirection="column">
                    <Box
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={resumoVisual.borderColor}
                      bg={resumoVisual.bg}
                      py={2.5}
                      px={3}
                      h="100%"
                      minH="80px"
                      flex="1"
                    >
                      <HStack mb={1} spacing={2} align="flex-start">
                        <Icon
                          as={resumoVisual.icon}
                          color={resumoVisual.iconColor}
                          boxSize={4}
                          mt={0.5}
                          flexShrink={0}
                        />
                        <Text
                          fontWeight="semibold"
                          fontSize="sm"
                          color={resumoVisual.titleColor}
                        >
                          {resumoVisual.title}
                        </Text>
                      </HStack>
                      <VStack align="stretch" spacing={0.5} w="100%" pl={6}>
                        <Box minH="2.25rem">
                          <Text
                            fontSize="xs"
                            color="orange.700"
                            lineHeight="short"
                            visibility={
                              caixaFechadoSelecionado ? "visible" : "hidden"
                            }
                          >
                            É necessário abrir o caixa antes de realizar um
                            reforço nesta conta. Selecione uma conta bancária ou
                            abra o caixa.
                          </Text>
                        </Box>
                        <Text fontSize="xs">
                          Saldo Disponível:{" "}
                          <Text as="span" fontWeight="semibold">
                            {formatCurrencyBR(saldoDisponivel, true)}
                          </Text>
                        </Text>
                        <Text fontSize="xs">
                          Valor do Reforço:{" "}
                          <Text as="span" fontWeight="semibold">
                            {formatCurrencyBR(valor, true)}
                          </Text>
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={feedbackNegativo ? "red.600" : "green.700"}
                        >
                          Saldo após operação:{" "}
                          {feedbackNegativo
                            ? `-${formatCurrencyBR(Math.abs(saldoApos), true)}`
                            : formatCurrencyBR(saldoApos, true)}
                        </Text>
                      </VStack>
                    </Box>
                  </GridItem>
                </Grid>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter py={3} px={4} gap={2} flexShrink={0} flexDirection={{ base: "column", sm: "row" }} alignItems={{ base: "stretch", sm: "center" }}>
            <Button
              variant="outline"
              colorScheme="blue"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={!pending && (
                checkingCaixa ||
                loadingContas ||
                !opcaoCaixa ||
                !caixaAberto ||
                !valor ||
                parseValue(valor) <= 0 ||
                !clinica ||
                !contaCaixa
              )}
            >
              {pending ? 'Reenviar reforço pendente' : 'Confirmar Reforço'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ReforcoModal;
